import React, { useContext, useMemo, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sider, Menu, Button } from "../../components/shared";
import { CloseOutlined } from "@ant-design/icons";
import { Drawer } from "antd";

import "./navbar.scss";
import { Liveline, LivelineMobile } from "../../assets/dark-theme";

import {
    LineChartOutlined,
    ControlOutlined,
    CalculatorOutlined,
    FallOutlined, MonitorOutlined, FileSearchOutlined, SlidersOutlined,
} from "@ant-design/icons";

import {
    users,
    Signal_Data_New_1,
    Signal_Data_Scada1,
    admin,
    cells,
    controls,
    tools,
    dashboard,
    equipment,
    facilities,
    historicalRunsIcon,
    liveHistoricalData,
    plug,
    products,
    rulesAndConfiguration,
    matrix,
    Alert,
    facilitiesGreyIcon,
    AdvancedController,
    VirtualSensor,
    LPP,
    MaterialTracking,
    LCPTrained,
    backTest,
    ProcessInsight
} from "../../assets/dark-theme";
import { FunctionOutlined } from "@ant-design/icons";

import { AbilityContext } from "../../components/shared/Can";
import { menuItemIsDisabled } from "../../components/shared/menu/MenuComponent";
import { userSelectionActions } from "../../state/features/userSelectionReducer";
import { ScreenKey } from "../../permissions/screenKeys";

// Type definitions
interface MenuItem {
    label: React.ReactNode;
    key: string;
    title: string;
    icon: React.ReactNode;
    children?: MenuItem[];
}

interface Facility {
    id: number;
    text: string;
    livelineFacilityId?: string;
}

interface UserSelection {
    facility: Facility;
    cell: any;
    product: any;
}

interface FacilityList {
    data: Facility[];
}

interface RootState {
    userSelection: UserSelection;
    facilityList: FacilityList;
}

interface LeftNavProps {
    onExpandMenu?: () => void;
    openKeys: string[];
    visibleSideMenu: boolean;
    mobileCollapse: boolean;
    isSmallScreen: boolean;
    isPortraitMode: boolean;
    onCollapseMenu?: () => void;
    selectedKey: string;
    closeMenu?: () => void;
}

const buildNavMenuItems = (t: (key: string) => string): MenuItem[] => [
    {
        label: t("navbar.dashboard"),
        key: ScreenKey.DASHBOARD,
        title: t("navbar.dashboard"),
        icon: <img src={dashboard} alt={t("navbar.dashboard")} className="sideMenuLogo" />,
    },
    {
        label: t("navbar.analytics"),
        key: ScreenKey.OEE_CONSOLE,
        title: t("navbar.analytics"),
        icon: (
            <LineChartOutlined
                className="sideMenuLogo"
                style={{ fontSize: "30px", color: "#c9c9c9" }}
            />
        ),
    },
    {
        label: t("navbar.admin"),
        key: "general_admin",
        title: t("navbar.admin"),
        icon: <img src={admin} alt={t("navbar.admin")} className="sideMenuLogo" />,
        children: [
            {
                label: t("navbar.facilities"),
                key: ScreenKey.FACILITIES,
                title: t("navbar.facilities"),
                icon: (
                    <img
                        src={facilities}
                        alt={t("navbar.facilities")}
                        className="sideMenuLogo"
                    />
                ),
            },
            {
                label: t("navbar.cells"),
                key: ScreenKey.CELLS,
                title: t("navbar.cells"),
                icon: <img src={cells} alt={t("navbar.cells")} className="sideMenuLogo" />,
            },
            {
                label: t("navbar.equipment"),
                key: ScreenKey.EQUIPMENT,
                title: t("navbar.equipment"),
                icon: (
                    <img
                        src={equipment}
                        alt={t("navbar.equipment")}
                        className="sideMenuLogo"
                    />
                ),
            },
            {
                label: t("navbar.products"),
                key: ScreenKey.PRODUCTS,
                title: t("navbar.products"),
                icon: (
                    <img
                        src={products}
                        alt={t("navbar.products")}
                        className="sideMenuLogo"
                    />
                ),
            },
            {
                label: t("navbar.users"),
                key: ScreenKey.USERS,
                title: t("navbar.users"),
                icon: (
                    <img
                        src={users}
                        alt={t("navbar.users")}
                        className="sideMenuLogo"
                        height={26}
                    />
                ),
            },
            {
                label: t("navbar.cellLayout"),
                key: ScreenKey.CELL_LAYOUT,
                title: t("navbar.cellLayout"),
                icon: (
                    <img
                        src={cells}
                        alt={t("navbar.cellLayout")}
                        className="sideMenuLogo"
                    />
                ),
            },
        ],
    },
    {
        label: t("navbar.monitor"),
        key: "live_historical_data",
        title: t("navbar.monitor"),
        icon: (
            <img
                src={liveHistoricalData}
                alt={t("navbar.monitor")}
                className="sideMenuLogo"
            />
        ),
        children: [
            {
                label: t("navbar.signalLayout"),
                key: ScreenKey.SIGNAL_LAYOUT,
                title: t("navbar.scada1Title"),
                icon: (
                    <img
                        src={Signal_Data_Scada1}
                        alt={t("navbar.signalLayout")}
                        className="sideMenuLogo"
                    />
                ),
            },
            {
                label: t("navbar.signalData"),
                key: ScreenKey.SIGNAL_DATA,
                title: t("navbar.scada2Title"),
                icon: (
                    <img
                        src={Signal_Data_New_1}
                        alt={t("navbar.signalData")}
                        className="sideMenuLogo"
                    />
                ),
            },
            {
                label: t("navbar.historicalRuns"),
                key: ScreenKey.HISTORICAL_RUNS,
                title: t("navbar.historicalRuns"),
                icon: (
                    <img
                        src={historicalRunsIcon}
                        alt={t("navbar.historicalRuns")}
                        className="sideMenuLogo"
                    />
                ),
            },
            {
                label: t("navbar.advancedControllers"),
                key: ScreenKey.ADVANCED_CONTROLLERS,
                title: t("navbar.advancedControllers"),
                icon: (
                    <img
                        src={AdvancedController}
                        alt={t("navbar.advancedControllers")}
                        className="sideMenuLogo"
                    />
                ),
            },
            {
                label: t("navbar.virtualSensor"),
                key: ScreenKey.VIRTUAL_SENSOR,
                title: t("navbar.virtualSensor"),
                icon: (
                    <img
                        src={VirtualSensor}
                        alt={t("navbar.virtualSensor")}
                        className="sideMenuLogo"
                    />
                ),
            },
            {
                label: t("navbar.liveServices"),
                key: ScreenKey.LIVE_SERVICES,
                title: t("navbar.liveServices"),
                icon: (
                    <img
                        src={plug}
                        alt={t("navbar.liveServices")}
                        className="sideMenuLogo"
                    />
                ),
            },
        ],
    },
    {
        label: t("navbar.controls"),
        key: "controls_admin",
        title: t("navbar.controls"),
        icon: <img src={controls} alt={t("navbar.controls")} className="sideMenuLogo" />,
        children: [
            {
                label: t("navbar.rules"),
                key: ScreenKey.RULES_CONFIGURATION,
                title: t("navbar.rulesConfigurationTitle"),
                icon: (
                    <img
                        src={rulesAndConfiguration}
                        alt={t("navbar.rules")}
                        className="sideMenuLogo"
                    />
                ),
            },
            {
                label: t("navbar.alerts"),
                key: ScreenKey.ALERTS_CONFIGURATION,
                title: t("navbar.alertsConfigurationTitle"),
                icon: (
                    <img
                        src={Alert}
                        alt={t("navbar.alerts")}
                        className="sideMenuLogo"
                    />
                ),
            },
            {
                label: t("navbar.processInsight"),
                key: ScreenKey.PROCESS_INSIGHT,
                title: t("navbar.processInsight"),
                icon: (
                    <img src={ProcessInsight} alt={t("navbar.processInsight")} className="sideMenuLogo" />
                ),
            },
            {
                label: t("navbar.stateActionMatrix"),
                key: ScreenKey.STATE_ACTION_MATRIX,
                title: t("navbar.stateActionMatrix"),
                icon: (
                    <img
                        src={matrix}
                        alt={t("navbar.stateActionMatrix")}
                        className="sideMenuLogo"
                    />
                ),
            },
            {
                label: t("navbar.cellControllerTuning"),
                key: ScreenKey.CELL_CONTROLLER_TUNING,
                title: t("navbar.cellControllerTuning"),
                icon: (
                    <ControlOutlined className="sideMenuLogo" style={{ fontSize: "30px", color: "#c9c9c9" }} />
                ),
            },
            {
                label: t("navbar.processInsightTemplates"),
                key: ScreenKey.PROCESS_INSIGHT_TEMPLATES,
                title: t("navbar.processInsightTemplates"),
                icon: (
                    <SlidersOutlined className="sideMenuLogo" style={{ fontSize: "30px", color: "#c9c9c9" }} />
                ),
            },
        ],
    },
    {
        // Root-level group of offline calculators. Both children are in
        // ALWAYS_VISIBLE_SCREENS, so this group is present for every user —
        // unlike the other groups, it never filters down to empty.
        label: t("navbar.tools"),
        key: "tools",
        title: t("navbar.tools"),
        icon: <img src={tools} alt={t("navbar.tools")} className="sideMenuLogo" />,
        children: [
            {
                label: t("navbar.pControllerCalculator"),
                key: ScreenKey.PROPORTIONAL_CONTROLLER_CALCULATOR,
                title: t("navbar.pControllerCalculatorTitle"),
                icon: (
                    <CalculatorOutlined className="sideMenuLogo" style={{ fontSize: "30px", color: "#c9c9c9" }} />
                ),
            },
            {
                label: t("navbar.rampLossCalculator"),
                key: ScreenKey.RAMP_LOSS_CALCULATOR,
                title: t("navbar.rampLossCalculatorTitle"),
                icon: (
                    <FallOutlined className="sideMenuLogo" style={{ fontSize: "30px", color: "#c9c9c9" }} />
                ),
            },
            {
                label: t("navbar.diagnostics"),
                key: ScreenKey.DIAGNOSTICS,
                title: t("navbar.diagnosticsTitle"),
                icon: (
                    <MonitorOutlined className="sideMenuLogo" style={{ fontSize: "30px", color: "#c9c9c9" }} />
                ),
            },
            // LT-3578 — a direct child of Tools, like Diagnostics. Not nested deeper: the Users
            // permission grid flattens this tree ONE level, so a deeper page vanishes from it (LT-3337).
            {
                label: t("navbar.signalReader"),
                key: ScreenKey.SIGNAL_READER,
                title: t("navbar.signalReaderTitle"),
                icon: (
                    <FileSearchOutlined className="sideMenuLogo" style={{ fontSize: "30px", color: "#c9c9c9" }} />
                ),
            },
        ],
    },
    {
        label: t("navbar.machineLearning"),
        key: 'ml',
        title: t("navbar.machineLearning"),
        icon: <FunctionOutlined className='sideMenuLogo' style={{ fontSize: "30px", color: "#c9c9c9" }} />,
        children: [
            {
                label: t("navbar.lpp"),
                key: ScreenKey.ML_LPP,
                title: t("navbar.lpp"),
                icon: <img src={LPP} alt={t("navbar.lpp")} className='sideMenuLogo' />,
            },
            {
                label: t("navbar.lcp"),
                key: ScreenKey.ML_LCP,
                title: t("navbar.lcp"),
                icon: <img src={LCPTrained} alt={t("navbar.lcp")} className='sideMenuLogo' />,
            },
            {
                label: t("navbar.backTest"),
                key: ScreenKey.ML_BACKTEST,
                title: t("navbar.backTest"),
                icon: <img src={backTest} alt={t("navbar.backTest")} className='sideMenuLogo' />,
            },
        ],
    },
    {
        label: t("navbar.materialTracking"),
        key: ScreenKey.MATERIAL_TRACKING,
        title: t("navbar.materialTracking"),
        icon: <img src={MaterialTracking} alt={t("navbar.materialTracking")} className="sideMenuLogo" />,
    },
];

/**
 * react-i18next returns a new `t` reference when the active language
 * changes, so this recomputes automatically on language switch.
 */
export const useNavMenuItems = (): MenuItem[] => {
    const { t } = useTranslation();
    return useMemo(() => buildNavMenuItems(t), [t]);
};

const LeftNav: React.FC<LeftNavProps> = ({
    onExpandMenu = () => {},
    openKeys,
    visibleSideMenu,
    mobileCollapse,
    isSmallScreen,
    isPortraitMode,
    onCollapseMenu = () => {},
    selectedKey,
    closeMenu = () => {},
}) => {
    const ability = useContext(AbilityContext);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const navMenuItems = useNavMenuItems();
    const userSelection = useSelector((state: RootState) => state?.userSelection);
    const facilityList = useSelector((state: RootState) => state?.facilityList);

    // Sort facility list to keep current selection at the top
    const facilityListMenu = [...facilityList?.data]?.sort((fA) =>
        userSelection
            ? userSelection.facility?.id === fA.id
                ? -1
                : 1
            : 1
    );

    // State for facility menu and mobile drawer
    const [isFacilityMenu, setIsFacilityMenu] = useState<boolean>(true);
    const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);

    const facilitySelectorMenuItem = useMemo(
        (): MenuItem => ({
            label: userSelection?.facility.text || t("navbar.selectFacility"),
            key: "facility_selector",
            title: t("navbar.selectFacility"),
            icon: (
                <img
                    src={facilitiesGreyIcon}
                    alt={t("navbar.selectFacility")}
                    className="sideMenuLogo"
                />
            ),
            children:
                facilityListMenu?.length >= 1 && isFacilityMenu
                    ? facilityListMenu.map((facility) => ({
                          label: facility.text,
                          key: `facility_selector/${facility.id}`,
                          title: t("navbar.selectFacilityItem", { name: facility.text }),
                          icon: <></>,
                      }))
                    : undefined,
        }),
        [facilityListMenu, userSelection?.facility, isFacilityMenu, t]
    );

    const accessibleMenuItems = useMemo(
        (): MenuItem[] =>
            navMenuItems.reduce(
                (prevMenuItems: MenuItem[], currMenuItem: MenuItem) => {
                    if (currMenuItem?.children?.length) {
                        const childItems = currMenuItem.children.filter(
                            ({ key }) => {
                                const isDisabled = !menuItemIsDisabled(
                                    key,
                                    ability,
                                    userSelection.facility?.livelineFacilityId
                                );
                                return isDisabled;
                            }
                        );

                        const retVal = [...prevMenuItems];

                        if (childItems.length) {
                            retVal.push({
                                ...currMenuItem,
                                children: childItems,
                            });
                        }

                        return retVal;
                    }

                    const retVal = [...prevMenuItems];

                    if (
                        !menuItemIsDisabled(
                            currMenuItem.key,
                            ability,
                            userSelection.facility?.livelineFacilityId
                        )
                    ) {
                        retVal.push(currMenuItem);
                    }
                    return retVal;
                },
                [facilitySelectorMenuItem]
            ),
        [ability, userSelection, facilitySelectorMenuItem, navMenuItems]
    );

    const onSelectHandler = (): void => {
        localStorage.setItem("isFacilityMenu", "true");
        setIsFacilityMenu(true);
        if (isPortraitMode) {
            onCollapseMenu(); // Close drawer when item is selected
        }
    };

    // Handle facility selection
    const handleFacilitySelection = (subItemKey: string): void => {
        const matches = subItemKey.match(/(?<=facility_selector\/)[\S]*/);
        
        if (matches && matches.length) {
            const id = matches[0];
            const selectedFacility = facilityList?.data?.find((f: Facility) => f.id === parseInt(id));
            
            if (selectedFacility) {
                dispatch(userSelectionActions.setUserSelection({
                    facility: selectedFacility,
                    cell: {},
                    product: {},
                }));
                
                // Check if current page is disabled for new facility
                if (menuItemIsDisabled(selectedKey, ability, selectedFacility.livelineFacilityId)) {
                    navigate('/');
                }
            }
        }
    };

    // Handle menu item click in drawer
    const handleDrawerMenuItemClick = (item: MenuItem): void => {
        if (item.children && item.children.length > 0) {
            setActiveSubmenu(activeSubmenu === item.key ? null : item.key);
        } else {
            // Close drawer and navigate
            onCollapseMenu();
            setActiveSubmenu(null);
            navigate(`/${item.key}`);
            
            // Update localStorage
            localStorage.setItem("isFacilityMenu", "true");
            setIsFacilityMenu(true);
        }
    };

    // Handle submenu item click in drawer
    const handleDrawerSubItemClick = (subItem: MenuItem): void => {
        // Close drawer
        onCollapseMenu();
        setActiveSubmenu(null);
        
        // Update localStorage
        localStorage.setItem("isFacilityMenu", "true");
        setIsFacilityMenu(true);
        
        if (subItem.key.startsWith('facility_selector/')) {
            handleFacilitySelection(subItem.key);
        } else {
            navigate(`/${subItem.key}`);
        }
    };

    useEffect(() => {
        const handleStorageChange = (): void => {
            const newIsFacilityMenu = localStorage.getItem("isFacilityMenu") === "true";
            setIsFacilityMenu(newIsFacilityMenu);
        };

        window.addEventListener("isFacilityMenuChange", handleStorageChange);

        return () => {
            window.removeEventListener("isFacilityMenuChange", handleStorageChange);
        };
    }, []);

    // Render mobile drawer for portrait mode
    if (isPortraitMode) {
        return (
            <>
                <Drawer
                    title={
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <img src={Liveline} alt="Liveline" style={{ height: '30px' }} />
                            <Button
                                type="text"
                                icon={<CloseOutlined />}
                                onClickEvent={onCollapseMenu}
                                style={{ color: '#fff' }}
                            />
                        </div>
                    }
                    placement="left"
                    open={!mobileCollapse}
                    onClose={onCollapseMenu}
                    closable={false}
                    width={280}
                    styles={{
                        header: {
                            backgroundColor: '#001529',
                            borderBottom: '1px solid #303030',
                            padding: '12px 16px',
                        },
                        body: {
                            backgroundColor: '#001529',
                            padding: 0,
                        }
                    }}
                >
                    <div className="mobile-drawer-content">
                        {accessibleMenuItems.map((item: MenuItem) => (
                            <div key={item.key} className="drawer-menu-item">
                                <div
                                    className={`drawer-item-header ${
                                        selectedKey === item.key || activeSubmenu === item.key ? 'active' : ''
                                    }`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleDrawerMenuItemClick(item);
                                    }}
                                >
                                    <div className="drawer-item-content">
                                        <span className="drawer-item-icon">
                                            {React.cloneElement(item.icon as React.ReactElement, {
                                                style: { 
                                                    fontSize: "20px", 
                                                    color: (selectedKey === item.key || activeSubmenu === item.key) ? "#fff" : "#c9c9c9" 
                                                }
                                            })}
                                        </span>
                                        <span className="drawer-item-label">{item.label}</span>
                                    </div>
                                    {item.children && item.children.length > 0 && (
                                        <span className="drawer-item-arrow">
                                            {activeSubmenu === item.key ? '▼' : '▶'}
                                        </span>
                                    )}
                                </div>

                                {/* Submenu */}
                                {activeSubmenu === item.key && item.children && item.children.length > 0 && (
                                    <div className="drawer-submenu">
                                        {item.children.map((subItem: MenuItem) => (
                                            <div
                                                key={subItem.key}
                                                className={`drawer-subitem ${
                                                    selectedKey === subItem.key ? 'active' : ''
                                                }`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleDrawerSubItemClick(subItem);
                                                }}
                                            >
                                                <span className="drawer-subitem-icon">
                                                    {subItem.icon && React.cloneElement(subItem.icon as React.ReactElement, {
                                                        style: { 
                                                            fontSize: "16px", 
                                                            color: selectedKey === subItem.key ? "#fff" : "#c9c9c9" 
                                                        }
                                                    })}
                                                </span>
                                                <span className="drawer-subitem-label">{subItem.label}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </Drawer>
            </>
        );
    }

    // Regular sidebar for desktop
    return (
        <div
            className={`side-menu ${
                visibleSideMenu ? "alignLeftNavCollapse" : "alignLeftNav"
            } ${mobileCollapse ? "mobileCollpase" : ""}`}
        >
            <Sider
                width={"15vw"}
                breakpoint="lg"
                collapsible
                collapsed={visibleSideMenu}
                onCollapse={onCollapseMenu}
            >
                {isSmallScreen ? (
                    <a href="/">
                        <img
                            src={LivelineMobile}
                            className="logo"
                            alt="livelinelog"
                        />
                    </a>
                ) : (
                    <a href="/">
                        {visibleSideMenu ? (
                            <img
                                src={LivelineMobile}
                                className="logo"
                                alt="livelinelog"
                            />
                        ) : (
                            <img
                                src={Liveline}
                                className="logo"
                                alt="livelinelog"
                            />
                        )}
                    </a>
                )}

                <Menu
                    style={{
                        maxHeight: "calc(100vh - 117px)",
                        overflow: "auto",
                    }}
                    items={accessibleMenuItems}
                    openKeys={openKeys}
                    onOpenChange={onExpandMenu}
                    defaultSelectedKeys={[selectedKey]}
                    closeMenu={closeMenu}
                    onSelectHandler={onSelectHandler}
                />
            </Sider>
        </div>
    );
};

export default LeftNav;